import { AGENT_HUB } from '@lumapps/agent-hub/keys';
import { ASK_AI_RULES } from '@lumapps/ask-ai-rules/keys';
import { DEFAULT_LANGUAGE, GLOBAL } from '@lumapps/translations';
import { TranslatableObject } from '@lumapps/translations/types';
import { ObjectValues } from '@lumapps/utils/types/ObjectValues';

import { AGENT_HUB_AGENTS } from './keys';
import {
    type AgentFormDefaultValues,
    type AgentStatusOption,
    type AgentTypeOption,
    AgentOrigin,
    AgentOriginOption,
    AgentStatus,
    AgentType,
} from './types';

/** Scope used for the agents admin */
export const SCOPE = 'search-agents-admin';

export const SEARCH_AGENTS_LIST_DEFAULT_SORT_ORDER = '-status,name';

/** Default max items fetched when fetching agents */
export const SEARCH_AGENTS_LIST_MAX_RESULTS = 20;

/** Minimum number of conversation starters required */
export const MIN_CONVERSATION_STARTERS = 1;

/** Maximum number of conversation starters allowed */
export const MAX_CONVERSATION_STARTERS = 4;

/** Maximum character length for a single conversation starter */
export const MAX_CONVERSATION_STARTER_LENGTH = 80;

/** Maximum number of tools to add to an agent before displaying a context warning */
export const MAX_TOOLS_BEFORE_WARNING = 10;

/** Empty conversation starter object template */
export const EMPTY_CONVERSATION_STARTER: TranslatableObject<string> = { translations: {} };

/** Default icon name for new agents */
export const DEFAULT_AGENT_ICON_NAME = 'robot-outline';

/** Default icon background color for new agents */
export const DEFAULT_AGENT_ICON_BACKGROUND_COLOR = 'rgba(0, 0, 0, 0.1)';

/** Default values for creating a new agent (type is selected by user) */
export const SEARCH_AGENT_DEFAULT_VALUES: AgentFormDefaultValues = {
    name: { translations: {} },
    description: { value: '', translations: {}, lang: DEFAULT_LANGUAGE },
    expertise: '',
    icon: { name: DEFAULT_AGENT_ICON_NAME, path: '' },
    visibleByGroups: [],
    status: AgentStatus.DRAFT,
    conversationStarters: [EMPTY_CONVERSATION_STARTER],
};

export const AGENT_NAME_MAX_LENGTH = 50;

/** Placeholder shown in the Agent Card URL field (language-independent URL, so not a translation key). */
export const AGENT_CARD_URL_PLACEHOLDER = 'https://example.com/.well-known/agent-card.json';

/**
 * Hidden probe message sent to an A2A agent to verify its connection over the normal chat flow.
 * Not user-facing (never shown in a conversation), so it is a constant rather than a translation key.
 */
export const A2A_PROBE_MESSAGE = 'Hello, who are you?';

/** Timeout (ms) after which an unresponsive A2A connection probe is treated as a failure. */
export const A2A_PROBE_TIMEOUT_MS = 30000;

/** `window.open` features for the A2A connector OAuth popup (mirrors the MCP connect popup). */
export const A2A_TEST_POPUP_OPTIONS =
    'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=600';

/**
 * Query-param flag added to the agent edit route after creating an A2A agent from the test flow.
 * Creating navigates to the edition form (page reload), so the flag survives the reload and lets the
 * connection test auto-resume there once the agent has an id.
 */
export const CONNECTION_TEST_QUERY_PARAM = 'connectionTest';

/** Maximum character length for the agent description shown in the agent library */
export const AGENT_LIBRARY_DESCRIPTION_MAX_LENGTH = 300;

export const MAXIMUM_SOURCES = 4;

/**
 * Single source of truth for how each agent type is presented to users:
 * - `label`: the descriptive name/mission used by the creation form (radio group)
 *   and the edit-mode type field.
 * - `description`: the form helper text shown under each type.
 * - `listLabel`: shorter label shown as the "Type" in the agent admin list.
 *   Falls back to `label` when omitted (RAG / REACT_STATELESS use the same wording
 *   in both places; EXTERNAL and STATIC_ANSWER are shortened in the list only).
 *
 * Front-office folds its types under dedicated group labels
 * (see `createFrontOfficeAgentTypeOptions`), so those are not derived here.
 */
export const AGENT_TYPE_DEFINITIONS: Record<AgentType, { label: string; description: string; listLabel?: string }> = {
    [AgentType.RAG]: {
        label: AGENT_HUB.SPECIALIZED_SEARCH_ASSISTANT_FOR_ASK_AI,
        description: AGENT_HUB.SPECIALIZED_SEARCH_ASSISTANT_FOR_ASK_AI_DESCRIPTION,
    },
    [AgentType.REACT_STATELESS]: {
        label: AGENT_HUB.AI_AGENT,
        description: AGENT_HUB.AI_AGENT_DESCRIPTION,
    },
    [AgentType.EXTERNAL]: {
        label: AGENT_HUB.LIST_AN_EXTERNAL_AGENT_IN_THE_LIBRARY,
        description: AGENT_HUB.LIST_AN_EXTERNAL_AGENT_IN_THE_LIBRARY_DESCRIPTION,
        listLabel: AGENT_HUB.EXTERNAL_AGENT,
    },
    [AgentType.EXTERNAL_A2A]: {
        label: AGENT_HUB_AGENTS.CONNECT_THIRD_PARTY_AGENT,
        description: AGENT_HUB_AGENTS.CONNECT_THIRD_PARTY_AGENT_DESCRIPTION,
        listLabel: AGENT_HUB.THIRD_PARTY_AGENT,
    },
    [AgentType.STATIC_ANSWER]: {
        label: AGENT_HUB_AGENTS.PROVIDE_CUSTOM_ANSWER,
        description: AGENT_HUB.PROVIDE_CUSTOM_ANSWER_DESCRIPTION,
        listLabel: ASK_AI_RULES.CUSTOM_ANSWER,
    },
};

/**
 * Create back-office agent type options
 * 3 options: external and A2A agents are folded into "AI Agent" (no standalone "External" option),
 * "Created by > External system" is used to filter external/A2A agents instead.
 * - "Specialized Search Assistant for AskAI": RAG
 * - "AI Agent": REACT_STATELESS + EXTERNAL + EXTERNAL_A2A (A2A is filtered like an external agent)
 * - "Custom answer": STATIC_ANSWER
 */
export const createBackOfficeAgentTypeOptions = (translateKey: (key: string) => string): AgentTypeOption[] => [
    {
        id: AgentType.RAG,
        label: translateKey(AGENT_HUB.SPECIALIZED_SEARCH_ASSISTANT_FOR_ASK_AI),
        types: [AgentType.RAG],
    },
    {
        id: 'ai-agents',
        label: translateKey(AGENT_HUB.AI_AGENT),
        types: [AgentType.REACT_STATELESS, AgentType.EXTERNAL, AgentType.EXTERNAL_A2A],
        isGroup: true,
    },
    {
        id: AgentType.STATIC_ANSWER,
        label: translateKey(ASK_AI_RULES.CUSTOM_ANSWER),
        types: [AgentType.STATIC_ANSWER],
    },
];

/**
 * Create "Created by" filter options (agent origin).
 * No dedicated backend param: each option id maps frontend-side onto existing `isNative` / `type`
 * params (see useAgentFilters).
 */
export const createAgentOriginOptions = (translateKey: (key: string) => string): AgentOriginOption[] => [
    {
        id: AgentOrigin.LUMAPPS,
        label: translateKey(GLOBAL.LUMAPPS),
    },
    {
        id: AgentOrigin.WORKSPACE_ADMINS,
        label: translateKey(AGENT_HUB_AGENTS.WORKSPACE_ADMINS),
    },
    {
        id: AgentOrigin.EXTERNAL_SYSTEM,
        label: translateKey(AGENT_HUB_AGENTS.EXTERNAL_SYSTEM),
    },
];

/**
 * Backend query represented by each "Created by" (origin) option (single-select).
 * There is no dedicated backend param, so each option is expressed via `isNative` + `type`:
 * - LumApps: native (built-in) agents
 * - Workspace admins: non-native agents created in the workspace (non-external types)
 * - External system: external and A2A agents (A2A is filtered like an external agent)
 *
 * `useAgentFilters` intersects the selected option's types with the Type filter.
 */
export const AGENT_ORIGIN_QUERY: Record<AgentOrigin, { isNative?: boolean; types?: AgentType[] }> = {
    [AgentOrigin.LUMAPPS]: { isNative: true },
    [AgentOrigin.WORKSPACE_ADMINS]: {
        isNative: false,
        types: [AgentType.RAG, AgentType.REACT_STATELESS, AgentType.STATIC_ANSWER],
    },
    [AgentOrigin.EXTERNAL_SYSTEM]: { isNative: false, types: [AgentType.EXTERNAL, AgentType.EXTERNAL_A2A] },
};

/**
 * Create agent status filter options
 */
export const createAgentStatusOptions = (translateKey: (key: string) => string): AgentStatusOption[] => [
    {
        id: AgentStatus.LIVE,
        label: translateKey(GLOBAL.PUBLISHED),
    },
    {
        id: AgentStatus.DRAFT,
        label: translateKey(GLOBAL.DRAFT),
    },
];

/**
 * Some agents are "static" and needs to be initiated by the frontend.
 * For these, we need to directly target their IDS.
 */
export const NATIVE_AGENTS = {
    askAi: {
        id: '0af756ae-3e8d-4bd1-9724-16e45ebc1908',
    },
    learning: {
        id: 'c839a10c-ee83-41a9-adf9-bb80533dbcb3',
    },
    activity: {
        id: '633194fb-adf9-48c2-bfcc-ea6ca43d45e2',
    },
    broadcast: {
        id: 'bcc7af44-eedc-4eab-9c35-ccd8d2995084',
    },
    pageWriter: {
        id: '73d8f32c-0842-4df1-84f6-223417796fe0',
    },
    pageWriterDesigner: {
        id: '0c501995-74bf-4793-9634-a16ead1c72c2',
    },
    playVideo: {
        id: '7b3823cb-8f15-5fd8-afcb-d2d49f4348d1',
    },
} as const;

/** Array of all agent types possible */
export const AGENT_TYPES_ARRAY = Object.values(AgentType);

/** Dialog steps of the A2A connection test flow. */
export const CONNECTION_TEST_STATE = {
    idle: 'idle',
    confirm: 'confirm',
    connecting: 'connecting',
    success: 'success',
    error: 'error',
} as const;
export type ConnectionTestState = ObjectValues<typeof CONNECTION_TEST_STATE>;

/** Persisted connection status shown next to the Test button. */
export const CONNECTION_STATUS = {
    untested: 'untested',
    connected: 'connected',
    failed: 'failed',
} as const;
export type ConnectionStatus = ObjectValues<typeof CONNECTION_STATUS>;
